import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { useErrorContext } from 'contexts/ErrorsNavigationContext/ErrorsNavigationContext';
import { WebEDIError } from 'services/webediErrors/types';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { DisplayModes } from 'components/DataTable/Types';
import { Button } from 'components/Form/Button/Button';
import { InputLabel } from 'components/Form/Input/InputLabel/InputLabel';
import { Translate } from 'components/Translate/Translate';

import { TextField } from './TextField';

type TextArrayFieldProps = {
  field: ui.TextArrayField;
  fieldId: string;
  isReadOnly: boolean;
  mode: DisplayModes;
  variant?: 'large';
};

export const TextArrayField = ({ field, fieldId, isReadOnly, mode, variant }: TextArrayFieldProps) => {
  const { t } = useTranslation();
  const {
    field: { onChange, value: rawValue },
  } = useController<{ [fieldId]: Array<string> }>({ name: fieldId });
  const { service } = useErrorContext<WebEDIError>();
  const arrayLevelError = service.useErrorById(fieldId);

  const elementField = { ...(field.type_config.elementField as ui.TextField), label: undefined };

  if (mode === DisplayModes.view) {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-xs text-gray-500">{field.label}</span>
        {rawValue?.length > 0 ? (
          <ul className="list-disc pl-4">
            {rawValue.map((value, index) => (
              <li key={index} className="text-sm text-gray-700">
                {value}
              </li>
            ))}
          </ul>
        ) : (
          <span className="text-sm text-gray-400">{EMPTY_FIELD}</span>
        )}
      </div>
    );
  }

  const value = rawValue?.length ? rawValue : [''];

  const handleAdd = () => onChange([...value, '']);

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-2">
      {variant === 'large' && (
        <div className="flex flex-col gap-1">
          <InputLabel label={field.label} variant={variant} required={field.modality === 'MANDATORY'} />
          {field.description && (
            <p className="text-sm text-gray-500">
              <Translate i18nKey={field.description} />
            </p>
          )}
          {arrayLevelError && <span className="text-sm text-red-600">{arrayLevelError.title}</span>}
        </div>
      )}

      {value.map((_, index) => (
        <div key={index} className="flex items-start gap-1">
          <div className="flex-1">
            <TextField
              field={elementField}
              fieldId={`${fieldId}.${index}`}
              isReadOnly={isReadOnly}
              mode={mode}
              variant={variant}
            />
          </div>
          {!isReadOnly && value.length > 1 && (
            <Button
              iconOnly
              size="small"
              variant="minimal"
              LeftIcon={XMarkIcon}
              onClick={() => handleRemove(index)}
              aria-label="Remove"
              analyticsId="schemable_form_text_array_remove"
              className="mt-1"
            >
              Remove
            </Button>
          )}
        </div>
      ))}

      {!isReadOnly && (
        <div>
          <Button
            size="extra-small"
            variant="secondary"
            LeftIcon={PlusIcon}
            onClick={handleAdd}
            analyticsId="schemable_form_text_array_add"
          >
            {field.type_config.addValueButtonLabel ?? t('common:form.addValue')}
          </Button>
        </div>
      )}
    </div>
  );
};
